import { PropsWithChildren, useEffect, useRef, useState } from 'react';
import { makeStyles } from '@material-ui/core';
import HomeIcon from '@material-ui/icons/Home';
import ExtensionIcon from '@material-ui/icons/Extension';
import LibraryBooks from '@material-ui/icons/LibraryBooks';
import CreateComponentIcon from '@material-ui/icons/AddCircleOutline';
import StorageIcon from '@material-ui/icons/Storage';
import CodeIcon from '@material-ui/icons/Code';
import CategoryIcon from '@material-ui/icons/Category';
import PsychologyIcon from '@material-ui/icons/Memory';
import AssignmentIcon from '@material-ui/icons/Assignment';
import LogoFull from './LogoFull';
import LogoIcon from './LogoIcon';
import {
  Settings as SidebarSettings,
  UserSettingsSignInAvatar,
} from '@backstage/plugin-user-settings';
import { SidebarSearchModal } from '@backstage/plugin-search';
import {
  Sidebar,
  sidebarConfig,
  SidebarDivider,
  SidebarGroup,
  SidebarItem,
  SidebarPage,
  SidebarSpace,
  useSidebarOpenState,
  Link,
} from '@backstage/core-components';
import MenuIcon from '@material-ui/icons/Menu';
import SearchIcon from '@material-ui/icons/Search';
import { MyGroupsSidebarItem } from '@backstage/plugin-org';
import GroupIcon from '@material-ui/icons/People';
import { NotificationsSidebarItem } from '@backstage/plugin-notifications';
import { useApi, identityApiRef } from '@backstage/core-plugin-api';

const OWNER_GROUP_REF = 'group:default/code4ev-owners';

function hasOwnerGroup(ownershipEntityRefs: string[] | undefined): boolean {
  if (!ownershipEntityRefs?.length) return false;
  return ownershipEntityRefs.includes(OWNER_GROUP_REF);
}

const useSidebarLogoStyles = makeStyles({
  root: {
    width: sidebarConfig.drawerWidthClosed,
    height: 3 * sidebarConfig.logoHeight,
    display: 'flex',
    flexFlow: 'row nowrap',
    alignItems: 'center',
    marginBottom: 18,
  },
  link: {
    width: sidebarConfig.drawerWidthClosed,
    marginLeft: 24,
  },
});

const SidebarLogo = () => {
  const classes = useSidebarLogoStyles();
  const { isOpen } = useSidebarOpenState();

  return (
    <div className={classes.root}>
      <Link to="/" underline="none" className={classes.link} aria-label="Home">
        {isOpen ? <LogoFull /> : <LogoIcon />}
      </Link>
    </div>
  );
};

export const Root = ({ children }: PropsWithChildren<{}>) => {
  const identityApi = useApi(identityApiRef);

  // Refresh ownership periodically so role changes are reflected without a
  // page reload. The backend resolves membership from its authoritative
  // user-info service rather than relying on a client-side catalog filter.
  const [groupKey, setGroupKey] = useState(0);
  const [isOwner, setIsOwner] = useState(false);
  const identityRef = useRef(identityApi);
  identityRef.current = identityApi;
  useEffect(() => {
    const refresh = async () => {
      try {
        const identity = await identityRef.current.getBackstageIdentity();
        let owner = hasOwnerGroup(identity.ownershipEntityRefs as string[] | undefined);

        // Resolve owner status server-side. This avoids a catalog refresh race
        // where the User entity has changed but a filtered relation query is
        // still served with the prior group membership.
        try {
          const { token } = await identityRef.current.getCredentials();
          const resp = await fetch('/api/infra/owner-status', {
            credentials: 'include',
            headers: token ? { Authorization: `Bearer ${token}` } : {},
          });
          if (resp.ok) {
            const data = await resp.json() as { owner?: boolean };
            owner = data.owner === true;
          }
        } catch {
          // Keep the fallback value from identity token claims.
        }

        setIsOwner(owner);
        setGroupKey(k => k + 1);
      } catch {
        setIsOwner(false);
        // Token refresh failed — session expired, Backstage will redirect to sign-in.
      }
    };
    void refresh();
    const interval = setInterval(refresh, 30_000);
    return () => clearInterval(interval);
  }, []);

  return (
  <SidebarPage>
    <Sidebar>
      <SidebarLogo />
      <SidebarGroup label="Search" icon={<SearchIcon />} to="/search">
        <SidebarSearchModal />
      </SidebarGroup>
      <SidebarDivider />
      <SidebarGroup label="Menu" icon={<MenuIcon />}>
        {/* Main */}
        <SidebarItem icon={HomeIcon} to="catalog" text="Home" />
        <SidebarDivider />
        {/* Catalog & repositories */}
        <SidebarItem icon={CategoryIcon} to="artifacts" text="Artifact" />
        <SidebarItem icon={StorageIcon} to="persistence" text="Persistence" />
        <SidebarDivider />
        {/* Development tools */}
        <SidebarItem icon={PsychologyIcon} to="ai-trainer" text="AI Trainer" />
        <SidebarItem icon={CodeIcon} to="co-ide" text="co-IDE" />
        <SidebarItem icon={CreateComponentIcon} to="create" text="New Component" />
        <SidebarDivider />
        {/* Reference */}
        <SidebarItem icon={ExtensionIcon} to="api-docs" text="APIs" />
        <SidebarItem icon={LibraryBooks} to="docs" text="Docs" />
        {isOwner ? (
          <SidebarItem icon={AssignmentIcon} to="audit-log" text="Audit Log" />
        ) : null}
        <MyGroupsSidebarItem
          key={groupKey}
          singularTitle="My Group"
          pluralTitle="My Groups"
          icon={GroupIcon}
        />
      </SidebarGroup>
      <SidebarSpace />
      <SidebarDivider />
      <NotificationsSidebarItem />
      <SidebarDivider />
      <SidebarGroup
        label="Settings"
        icon={<UserSettingsSignInAvatar />}
        to="/settings"
      >
        <SidebarSettings />
      </SidebarGroup>
    </Sidebar>
    {children}
  </SidebarPage>
  );
};
